import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import Layout from "@/components/Layout";
import ScrollToTop from "@/components/ScrollToTop";
import Home from "@/pages/Home";
import ServicesIndex from "@/pages/ServicesIndex";
import ServiceDetail from "@/pages/ServiceDetail";
import Process from "@/pages/Process";
import About from "@/pages/About";
import Insights from "@/pages/Insights";
import InsightDetail from "@/pages/InsightDetail";
import Contact from "@/pages/Contact";
import Reviews from "@/pages/Reviews";
import LeaveAReview from "@/pages/LeaveAReview";
import BrandsIndex from "@/pages/BrandsIndex";
import BrandDetail from "@/pages/BrandDetail";
import StubPage from "@/pages/StubPage";
import ConciergeLogin from "@/pages/admin/ConciergeLogin";
import ConciergeDashboard from "@/pages/admin/ConciergeDashboard";
import PrivateAccess from "@/pages/private/PrivateAccess";
import PrivateIndex from "@/pages/private/PrivateIndex";
import PrivateProjectDetail from "@/pages/private/PrivateProjectDetail";
import PrivateLogout from "@/pages/private/PrivateLogout";

// Invoice admin routes — lazy-loaded so the invoice UI never lands in the
// public main.js bundle. Kept behind /concierge/invoices* which is admin-only
// and JWT-guarded server-side anyway.
const InvoicesList = lazy(() => import("@/pages/admin/InvoicesList"));
const InvoiceNew = lazy(() => import("@/pages/admin/InvoiceNew"));
const InvoiceDetail = lazy(() => import("@/pages/admin/InvoiceDetail"));
const AdminUsersList = lazy(() => import("@/pages/admin/AdminUsersList"));
const AdminPasscodesList = lazy(() => import("@/pages/admin/AdminPasscodesList"));
const ProposalsList = lazy(() => import("@/pages/admin/ProposalsList"));
const ProposalForm = lazy(() => import("@/pages/admin/ProposalForm"));
const ProposalDetail = lazy(() => import("@/pages/admin/ProposalDetail"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));

function AdminSuspense({ children }) {
    return (
        <Suspense
            fallback={
                <div
                    className="min-h-screen bg-[#0B0B0C] flex items-center justify-center"
                    data-testid="admin-lazy-loading"
                >
                    <div className="w-32 h-px bg-[rgba(201,168,106,0.3)] relative overflow-hidden">
                        <span className="absolute inset-y-0 left-0 w-1/3 bg-[#C9A86A] animate-[slide_1.2s_ease-in-out_infinite]" />
                    </div>
                </div>
            }
        >
            {children}
        </Suspense>
    );
}

function App() {
    return (
        <HelmetProvider>
            <div className="App">
                <BrowserRouter>
                    <ScrollToTop />
                    <Routes>
                        {/* Public site — wrapped in Layout (Nav + Footer) */}
                        <Route element={<Layout />}>
                            <Route path="/" element={<Home />} />
                            <Route
                                path="/services"
                                element={<ServicesIndex />}
                            />
                            <Route
                                path="/services/:slug"
                                element={<ServiceDetail />}
                            />
                            <Route path="/process" element={<Process />} />
                            <Route path="/about" element={<About />} />
                            <Route path="/insights" element={<Insights />} />
                            <Route
                                path="/insights/:slug"
                                element={<InsightDetail />}
                            />
                            <Route path="/contact" element={<Contact />} />
                            <Route path="/reviews" element={<Reviews />} />
                            <Route
                                path="/leave-a-review"
                                element={<LeaveAReview />}
                            />
                            <Route path="/brands" element={<BrandsIndex />} />
                            <Route
                                path="/brands/:slug"
                                element={<BrandDetail />}
                            />
                            <Route
                                path="*"
                                element={
                                    <StubPage
                                        eyebrow="Not Found"
                                        headline="This page is not yet composed."
                                        body="The address you followed does not lead anywhere we&rsquo;ve finished. Please return home or contact the concierge."
                                        testId="page-notfound"
                                    />
                                }
                            />
                        </Route>

                        {/* Concierge admin — rendered WITHOUT the public Layout.
                            No nav, no footer — intentionally hidden from the
                            public site map. */}
                        <Route
                            path="/concierge/login"
                            element={<ConciergeLogin />}
                        />
                        <Route
                            path="/concierge"
                            element={<ConciergeDashboard />}
                        />
                        <Route
                            path="/concierge/invoices"
                            element={
                                <AdminSuspense>
                                    <InvoicesList />
                                </AdminSuspense>
                            }
                        />
                        <Route
                            path="/concierge/invoices/new"
                            element={
                                <AdminSuspense>
                                    <InvoiceNew />
                                </AdminSuspense>
                            }
                        />
                        <Route
                            path="/concierge/invoices/:id"
                            element={
                                <AdminSuspense>
                                    <InvoiceDetail />
                                </AdminSuspense>
                            }
                        />
                        <Route
                            path="/concierge/access/users"
                            element={
                                <AdminSuspense>
                                    <AdminUsersList />
                                </AdminSuspense>
                            }
                        />
                        <Route
                            path="/concierge/access/passcodes"
                            element={
                                <AdminSuspense>
                                    <AdminPasscodesList />
                                </AdminSuspense>
                            }
                        />
                        <Route
                            path="/concierge/proposals"
                            element={
                                <AdminSuspense>
                                    <ProposalsList />
                                </AdminSuspense>
                            }
                        />
                        <Route
                            path="/concierge/proposals/new"
                            element={
                                <AdminSuspense>
                                    <ProposalForm />
                                </AdminSuspense>
                            }
                        />
                        <Route
                            path="/concierge/proposals/:id"
                            element={
                                <AdminSuspense>
                                    <ProposalDetail />
                                </AdminSuspense>
                            }
                        />
                        <Route
                            path="/concierge/proposals/:id/edit"
                            element={
                                <AdminSuspense>
                                    <ProposalForm />
                                </AdminSuspense>
                            }
                        />
                        <Route
                            path="/concierge/reviews"
                            element={
                                <AdminSuspense>
                                    <AdminReviews />
                                </AdminSuspense>
                            }
                        />

                        {/* Private Portfolio — also outside the public Layout.
                            NDA-gated, separate `act_nda` cookie. */}
                        <Route
                            path="/private/access"
                            element={<PrivateAccess />}
                        />
                        <Route path="/private" element={<PrivateIndex />} />
                        <Route
                            path="/private/projects/:slug"
                            element={<PrivateProjectDetail />}
                        />
                        <Route
                            path="/private/logout"
                            element={<PrivateLogout />}
                        />
                    </Routes>
                </BrowserRouter>
            </div>
        </HelmetProvider>
    );
}

export default App;
